import { unitsData } from '../data/lessons/lessonData.ts';
import { DEFAULT_META, DEFAULT_PROGRESS, UserData, UserMeta, UserProfile, UserProgress } from '../types/userData.ts';

const USER_DATA_KEY = 'galaxy_aksara_user_data';

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDateString = (): string => {
    return new Date().toISOString().split('T')[0];
};

// Helper function to calculate days between two dates
const getDaysBetween = (date1: string, date2: string): number => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get user data from localStorage and update streak if needed
 */
export const getUserData = (): UserData | null => {
    try {
        const data = localStorage.getItem(USER_DATA_KEY);
        if (!data) return null;

        const userData = JSON.parse(data) as UserData;

        // Ensure all required fields exist
        if (!userData.profile?.username || !userData.profile?.photo) {
            return null;
        }

        // Ensure progress object exists with default values
        userData.progress = {
            aksaraCompleted: userData.progress?.aksaraCompleted || [],
            achievements: userData.progress?.achievements || [],
            coursesCompleted: userData.progress?.coursesCompleted || [],
            lessonProgress: userData.progress?.lessonProgress || {}, // New field for lesson completion counts
        };

        // Migration: If a lesson exists in coursesCompleted but not in lessonProgress, 
        // it was completed with the old system, so set it to 5 completions
        const migratedLessons: string[] = [];
        userData.progress.coursesCompleted.forEach(lessonId => {
            if (!(lessonId in userData.progress.lessonProgress)) {
                userData.progress.lessonProgress[lessonId] = 5;
                migratedLessons.push(lessonId);
            }
        });

        if (migratedLessons.length > 0) {
            console.log('🔄 Migrated lessons from old system:', migratedLessons);
            // Save the migrated data
            localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        }

        // Ensure meta object exists with default values
        userData.meta = {
            money: userData.meta?.money ?? DEFAULT_META.money,
            heart: userData.meta?.heart ?? DEFAULT_META.heart,
            streak: {
                current: userData.meta?.streak?.current ?? DEFAULT_META.streak.current,
                lastLoginDate: userData.meta?.streak?.lastLoginDate ?? DEFAULT_META.streak.lastLoginDate,
            },
            xp: userData.meta?.xp ?? DEFAULT_META.xp,
            level: userData.meta?.level ?? DEFAULT_META.level,
            heartRecovery: {
                lastRecoveryTime: userData.meta?.heartRecovery?.lastRecoveryTime ?? DEFAULT_META.heartRecovery.lastRecoveryTime,
                nextRecoveryTime: userData.meta?.heartRecovery?.nextRecoveryTime,
            },
            darkMode: userData.meta?.darkMode ?? DEFAULT_META.darkMode,
        };

        // Update heart recovery and streak
        const updatedUserData = updateHeartRecovery(updateLoginStreak(userData));

        // Save updated data if streak was modified
        if (updatedUserData !== userData) {
            saveUserData(updatedUserData);
            return updatedUserData;
        }

        return userData;
    } catch (error) {
        console.error('Error reading user data from localStorage:', error);
        return null;
    }
};

/**
 * Update login streak based on current date
 */
const updateLoginStreak = (userData: UserData): UserData => {
    const today = getTodayDateString();
    const lastLogin = userData.meta.streak.lastLoginDate;

    if (lastLogin === today) {
        // Same day - no change
        return userData;
    }

    const daysSinceLastLogin = getDaysBetween(lastLogin, today);

    if (daysSinceLastLogin === 1) {
        // Next day - increment streak
        userData.meta.streak.current += 1;
        userData.meta.streak.lastLoginDate = today;
    } else if (daysSinceLastLogin > 1) {
        // More than 1 day - reset streak
        userData.meta.streak.current = 1;
        userData.meta.streak.lastLoginDate = today;
    }

    return userData;
};

/**
 * Update heart recovery based on time elapsed
 */
const updateHeartRecovery = (userData: UserData): UserData => {
    const now = new Date();
    const maxHearts = 5;
    const recoveryIntervalMs = 2 * 60 * 1000; // 2 minutes in milliseconds

    // If user already has max hearts, just update the recovery time
    if (userData.meta.heart >= maxHearts) {
        userData.meta.heartRecovery.lastRecoveryTime = now.toISOString();
        userData.meta.heartRecovery.nextRecoveryTime = undefined;
        return userData;
    }

    const lastRecoveryTime = new Date(userData.meta.heartRecovery.lastRecoveryTime);
    const timeSinceLastRecovery = now.getTime() - lastRecoveryTime.getTime();

    // Calculate how many hearts should be recovered
    const heartsToRecover = Math.floor(timeSinceLastRecovery / recoveryIntervalMs);

    if (heartsToRecover > 0) {
        // Add hearts up to maximum
        const newHeartCount = Math.min(maxHearts, userData.meta.heart + heartsToRecover);
        userData.meta.heart = newHeartCount;

        // Update last recovery time to account for recovered hearts
        const recoveredIntervals = newHeartCount - (userData.meta.heart - heartsToRecover);
        userData.meta.heartRecovery.lastRecoveryTime = new Date(
            lastRecoveryTime.getTime() + (recoveredIntervals * recoveryIntervalMs)
        ).toISOString();

        console.log(`💖 Recovered ${heartsToRecover} heart(s). Current hearts: ${newHeartCount}/${maxHearts}`);
    }

    // Calculate next recovery time if not at max hearts
    if (userData.meta.heart < maxHearts) {
        const nextRecoveryTime = new Date(
            new Date(userData.meta.heartRecovery.lastRecoveryTime).getTime() + recoveryIntervalMs
        );
        userData.meta.heartRecovery.nextRecoveryTime = nextRecoveryTime.toISOString();
    } else {
        userData.meta.heartRecovery.nextRecoveryTime = undefined;
    }

    return userData;
};

/**
 * Save user data to localStorage
 */
export const saveUserData = (userData: UserData): boolean => {
    try {
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

        // Dispatch custom event to notify components of data change
        window.dispatchEvent(new Event('userDataChanged'));

        return true;
    } catch (error) {
        console.error('Error saving user data to localStorage:', error);
        return false;
    }
};

/**
 * Create initial user data
 */
export const createUserData = (profile: UserProfile): UserData => {
    return {
        profile,
        progress: { ...DEFAULT_PROGRESS },
        meta: { ...DEFAULT_META },
    };
};

/**
 * Check if user data exists
 */
export const hasUserData = (): boolean => {
    return getUserData() !== null;
};

/**
 * Clear all user data (for logout/reset)
 */
export const clearUserData = (): void => {
    localStorage.removeItem(USER_DATA_KEY);
};

/**
 * Update user profile
 */
export const updateUserProfile = (profileUpdates: Partial<UserProfile>): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    userData.profile = { ...userData.profile, ...profileUpdates };
    return saveUserData(userData);
};

/**
 * Helper function to update any progress field (only for array fields)
 */
export const updateProgressField = (field: 'aksaraCompleted' | 'achievements' | 'coursesCompleted', value: string): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    if (!userData.progress[field].includes(value)) {
        userData.progress[field].push(value);
        return saveUserData(userData);
    }

    return true; // Already exists
};

/**
 * Helper function to update meta fields
 */
export const updateMetaField = (updates: Partial<UserMeta>): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    userData.meta = { ...userData.meta, ...updates };
    return saveUserData(userData);
};

/**
 * Add completed aksara ID (using consistent ID format)
 */
export const addAksaraCompleted = (aksaraId: string | number): boolean => {
    const id = String(aksaraId); // Ensure consistent string format
    return updateProgressField('aksaraCompleted', id);
};

/**
 * Add achievement ID (using consistent ID format matching achievementData.ts)
 */
export const addAchievement = (achievementId: string | number): boolean => {
    const id = String(achievementId); // Ensure consistent string format
    return updateProgressField('achievements', id);
};

/**
 * Add completed course ID (using consistent ID format)
 */
export const addCourseCompleted = (courseId: string): boolean => {
    return updateProgressField('coursesCompleted', courseId);
};

/**
 * Money management functions
 */
export const addMoney = (amount: number): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    const newAmount = Math.max(0, userData.meta.money + amount);
    return updateMetaField({ money: newAmount });
};

export const spendMoney = (amount: number): boolean => {
    const userData = getUserData();
    if (!userData || userData.meta.money < amount) return false;

    const newAmount = userData.meta.money - amount;
    return updateMetaField({ money: newAmount });
};

/**
 * Heart management functions
 */
export const addHeart = (amount: number = 1): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    const newAmount = Math.min(5, userData.meta.heart + amount); // Max 5 hearts
    return updateMetaField({ heart: newAmount });
};

export const loseHeart = (amount: number = 1): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    const newAmount = Math.max(0, userData.meta.heart - amount);

    // Update heart recovery time when hearts are lost
    const success = updateMetaField({
        heart: newAmount,
        heartRecovery: {
            lastRecoveryTime: new Date().toISOString(),
            nextRecoveryTime: newAmount < 5 ? new Date(Date.now() + 2 * 60 * 1000).toISOString() : undefined
        }
    });

    return success;
};

/**
 * Purchase hearts with in-game currency
 */
export const purchaseHearts = (heartsToBuy: number = 1): { success: boolean; message: string; cost: number } => {
    const userData = getUserData();
    if (!userData) return { success: false, message: "No user data found", cost: 0 };

    const heartCost = 50; // Cost per heart - balanced against earning rate (lessons give ~10-15 money)
    const totalCost = heartsToBuy * heartCost;
    const maxHearts = 5;

    // Check if user has enough money
    if (userData.meta.money < totalCost) {
        return {
            success: false,
            message: `Tidak cukup uang. Butuh ${totalCost}, punya ${userData.meta.money}`,
            cost: totalCost
        };
    }

    // Check if user is already at max hearts
    if (userData.meta.heart >= maxHearts) {
        return {
            success: false,
            message: "Hati sudah penuh!",
            cost: totalCost
        };
    }

    // Calculate actual hearts that can be bought (respect max cap)
    const actualHeartsToBuy = Math.min(heartsToBuy, maxHearts - userData.meta.heart);
    const actualCost = actualHeartsToBuy * heartCost;

    // Purchase hearts
    const newHeartCount = userData.meta.heart + actualHeartsToBuy;
    const newMoney = userData.meta.money - actualCost;

    const success = updateMetaField({
        heart: newHeartCount,
        money: newMoney,
        heartRecovery: {
            lastRecoveryTime: new Date().toISOString(),
            nextRecoveryTime: newHeartCount < maxHearts ? new Date(Date.now() + 2 * 60 * 1000).toISOString() : undefined
        }
    });

    if (success) {
        console.log(`💖 Purchased ${actualHeartsToBuy} heart(s) for ${actualCost} money. Current hearts: ${newHeartCount}/${maxHearts}`);
        return {
            success: true,
            message: `Berhasil membeli ${actualHeartsToBuy} hati untuk ${actualCost} koin!`,
            cost: actualCost
        };
    }

    return {
        success: false,
        message: "Gagal membeli hati",
        cost: actualCost
    };
};

/**
 * Get heart recovery information
 */
export const getHeartRecoveryInfo = (): {
    currentHearts: number;
    maxHearts: number;
    timeUntilNextHeart: number;
    nextHeartTime?: Date;
    isRecovering: boolean;
} => {
    const userData = getUserData();
    if (!userData) {
        return {
            currentHearts: 5,
            maxHearts: 5,
            timeUntilNextHeart: 0,
            isRecovering: false
        };
    }

    const maxHearts = 5;
    const recoveryIntervalMs = 2 * 60 * 1000; // 2 minutes

    if (userData.meta.heart >= maxHearts) {
        return {
            currentHearts: userData.meta.heart,
            maxHearts,
            timeUntilNextHeart: 0,
            isRecovering: false
        };
    }

    const now = new Date();
    const nextRecoveryTime = userData.meta.heartRecovery.nextRecoveryTime
        ? new Date(userData.meta.heartRecovery.nextRecoveryTime)
        : new Date(new Date(userData.meta.heartRecovery.lastRecoveryTime).getTime() + recoveryIntervalMs);

    const timeUntilNext = Math.max(0, nextRecoveryTime.getTime() - now.getTime());

    return {
        currentHearts: userData.meta.heart,
        maxHearts,
        timeUntilNextHeart: timeUntilNext,
        nextHeartTime: timeUntilNext > 0 ? nextRecoveryTime : undefined,
        isRecovering: timeUntilNext > 0
    };
};

/**
 * Format time remaining for heart recovery
 */
export const formatHeartRecoveryTime = (milliseconds: number): string => {
    if (milliseconds <= 0) return "0:00";

    const minutes = Math.floor(milliseconds / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Streak management functions
 */
export const getCurrentStreak = (): number => {
    const userData = getUserData();
    return userData?.meta.streak.current || 0;
};

export const resetStreak = (): boolean => {
    return updateMetaField({
        streak: {
            current: 0,
            lastLoginDate: getTodayDateString()
        }
    });
};

/**
 * XP and Level management functions
 */

// XP required for each level (exponential growth)
const getXpRequiredForLevel = (level: number): number => {
    return Math.floor(100 * Math.pow(1.2, level - 1));
};

// Calculate level from total XP
const calculateLevelFromXp = (totalXp: number): number => {
    let level = 1;
    let xpNeeded = 0;

    while (xpNeeded <= totalXp) {
        xpNeeded += getXpRequiredForLevel(level);
        if (xpNeeded <= totalXp) {
            level++;
        }
    }

    return level;
};

// Get XP progress for current level
const getXpProgressForCurrentLevel = (totalXp: number, currentLevel: number): { currentXp: number, xpToNextLevel: number } => {
    let xpUsed = 0;

    // Calculate XP used for previous levels
    for (let i = 1; i < currentLevel; i++) {
        xpUsed += getXpRequiredForLevel(i);
    }

    const currentXp = totalXp - xpUsed;
    const xpToNextLevel = getXpRequiredForLevel(currentLevel);

    return { currentXp, xpToNextLevel };
};

export const addXp = (amount: number): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    const newTotalXp = userData.meta.xp + amount;
    const newLevel = calculateLevelFromXp(newTotalXp);

    // Check if user leveled up
    const leveledUp = newLevel > userData.meta.level;

    const success = updateMetaField({
        xp: newTotalXp,
        level: newLevel
    });

    if (success && leveledUp) {
        console.log(`🎉 Level up! User reached level ${newLevel}`);
        // You can add level up rewards here
        // For example: addMoney(newLevel * 10);
    }

    return success;
};

export const getUserLevel = (): number => {
    const userData = getUserData();
    return userData?.meta.level || 1;
};

export const getUserXp = (): number => {
    const userData = getUserData();
    return userData?.meta.xp || 0;
};

export const getXpProgress = (): { currentXp: number, xpToNextLevel: number, totalXp: number, level: number, progressPercent: number } => {
    const userData = getUserData();
    if (!userData) return { currentXp: 0, xpToNextLevel: 100, totalXp: 0, level: 1, progressPercent: 0 };

    const totalXp = userData.meta.xp;
    const level = userData.meta.level;
    const { currentXp, xpToNextLevel } = getXpProgressForCurrentLevel(totalXp, level);
    const progressPercent = Math.min((currentXp / xpToNextLevel) * 100, 100);

    return { currentXp, xpToNextLevel, totalXp, level, progressPercent };
};

/**
 * Get user progress
 */
export const getUserProgress = (): UserProgress | null => {
    const userData = getUserData();
    return userData?.progress || null;
};

/**
 * Get user profile
 */
export const getUserProfile = (): UserProfile | null => {
    const userData = getUserData();
    return userData?.profile || null;
};

/**
 * Get user meta information
 */
export const getUserMeta = (): UserMeta | null => {
    const userData = getUserData();
    return userData?.meta || null;
};

/**
 * Check if specific aksara is completed
 */
export const isAksaraCompleted = (aksaraId: string | number): boolean => {
    const userData = getUserData();
    const id = String(aksaraId);
    return userData?.progress.aksaraCompleted.includes(id) || false;
};

/**
 * Check if specific achievement is earned
 */
export const isAchievementEarned = (achievementId: string | number): boolean => {
    const userData = getUserData();
    const id = String(achievementId);
    return userData?.progress.achievements.includes(id) || false;
};

/**
 * Check if specific course is completed
 */
export const isCourseCompleted = (courseId: string): boolean => {
    const userData = getUserData();
    return userData?.progress.coursesCompleted.includes(courseId) || false;
};

/**
 * Get completion statistics
 */
export const getCompletionStats = () => {
    const userData = getUserData();
    if (!userData) return { aksara: 0, achievements: 0, courses: 0, total: 0 };

    return {
        aksara: userData.progress.aksaraCompleted.length,
        achievements: userData.progress.achievements.length,
        courses: userData.progress.coursesCompleted.length,
        total: userData.progress.aksaraCompleted.length +
            userData.progress.achievements.length +
            userData.progress.coursesCompleted.length,
    };
};

/**
 * Update selected language
 */
export const updateSelectedLanguage = (language: string): boolean => {
    const userData = getUserData();
    if (!userData) return false;

    userData.profile.selectedLanguage = language;
    console.log('🌐 Language updated:', language); // Debug log
    return saveUserData(userData);
};

/**
 * Get selected language
 */
export const getSelectedLanguage = (): string | null => {
    const userData = getUserData();
    const language = userData?.profile.selectedLanguage || null;
    console.log('🌐 Current selected language:', language); // Debug log
    return language;
};

/**
 * Check if user has completed onboarding (has username, photo, and language)
 */
export const hasCompletedOnboarding = (): boolean => {
    const userData = getUserData();
    const isComplete = !!(
        userData?.profile.username &&
        userData?.profile.photo &&
        userData?.profile.selectedLanguage
    );
    console.log('✅ Onboarding complete:', isComplete); // Debug log
    return isComplete;
};

/**
 * Lesson Progress Management (New System)
 * Each lesson requires 5 completions to be fully finished
 */

/**
 * Get lesson completion count (0-5)
 */
export const getLessonCompletionCount = (lessonId: string): number => {
    const userData = getUserData();
    if (!userData) return 0;

    const count = userData.progress.lessonProgress[lessonId] || 0;
    console.log(`📊 getLessonCompletionCount(${lessonId}): ${count}`);
    return count;
};/**
 * Increment lesson completion count
 * Returns the new completion count
 */
export const incrementLessonCompletion = (lessonId: string): number => {
    console.log('📚 incrementLessonCompletion called for:', lessonId);
    console.log('📍 Call stack:', new Error().stack);

    const userData = getUserData();
    if (!userData) {
        console.error('❌ No user data found');
        return 0;
    }

    const currentCount = userData.progress.lessonProgress[lessonId] || 0;
    const newCount = Math.min(5, currentCount + 1); // Cap at 5

    console.log(`📊 Incrementing ${lessonId}: ${currentCount} → ${newCount}`);

    userData.progress.lessonProgress[lessonId] = newCount;

    // If lesson reaches 5 completions, add it to the old coursesCompleted array for backward compatibility
    if (newCount === 5 && !userData.progress.coursesCompleted.includes(lessonId)) {
        userData.progress.coursesCompleted.push(lessonId);
        console.log(`🎉 Lesson ${lessonId} fully completed and added to coursesCompleted`);
    }

    const success = saveUserData(userData);

    if (success) {
        console.log(`✅ Lesson ${lessonId} completion saved: ${newCount}/5`);
    } else {
        console.error('❌ Failed to save lesson progress');
    }

    return newCount;
};

/**
 * Check if lesson is fully completed (5/5)
 */
export const isLessonFullyCompleted = (lessonId: string): boolean => {
    const completionCount = getLessonCompletionCount(lessonId);
    const isFullyCompleted = completionCount >= 5;
    console.log(`🔍 isLessonFullyCompleted(${lessonId}): ${completionCount}/5 = ${isFullyCompleted}`);
    return isFullyCompleted;
};

/**
 * Reset lesson progress (for debugging)
 */
export const resetLessonProgress = (lessonId: string): boolean => {
    console.log('🔄 Resetting lesson progress:', lessonId);

    const userData = getUserData();
    if (!userData) return false;

    // Remove from lessonProgress
    delete userData.progress.lessonProgress[lessonId];

    // Remove from coursesCompleted (old system)
    const index = userData.progress.coursesCompleted.indexOf(lessonId);
    if (index > -1) {
        userData.progress.coursesCompleted.splice(index, 1);
    }

    const success = saveUserData(userData);
    if (success) {
        console.log('✅ Lesson progress reset successfully:', lessonId);
    }
    return success;
};

/**
 * Lesson Progress Management (Legacy System - kept for compatibility)
 */

/**
 * Add completed lesson ID
 */
export const addLessonCompleted = (lessonId: string): boolean => {
    console.log('📚 Marking lesson as completed:', lessonId); // Debug log

    const userData = getUserData();
    if (!userData) {
        console.error('❌ No user data found');
        return false;
    }

    // Check if lesson is already completed
    if (userData.progress.coursesCompleted.includes(lessonId)) {
        console.warn('⚠️ Lesson already completed:', lessonId);
        return true; // Return true since it's already completed
    }

    // Add the lesson to completed list
    const success = updateProgressField('coursesCompleted', lessonId);

    if (success) {
        console.log('✅ Lesson completed successfully:', lessonId);
        // The userDataChanged event is already dispatched by saveUserData()
    } else {
        console.error('❌ Failed to mark lesson as completed:', lessonId);
    }

    return success;
};

/**
 * Check if specific lesson is completed
 */
export const isLessonCompleted = (lessonId: string): boolean => {
    const userData = getUserData();
    const isCompleted = userData?.progress.coursesCompleted.includes(lessonId) || false;
    console.log('🔍 Lesson completion check:', { lessonId, isCompleted }); // Debug log
    return isCompleted;
};

/**
 * Check if an entire unit is completed (all lessons in the unit are fully completed)
 */
export const isUnitCompleted = (unitId: string): boolean => {
    const unit = unitsData.find(u => u.id === unitId);
    if (!unit) return false;

    // Check if all lessons in the unit are fully completed
    return unit.lessons.every(lesson => isLessonFullyCompleted(lesson.id));
};

/**
 * Get the previous unit ID based on unit order in unitsData
 */
const getPreviousUnitId = (currentUnitId: string): string | null => {
    const currentUnitIndex = unitsData.findIndex(u => u.id === currentUnitId);
    if (currentUnitIndex <= 0) return null; // First unit or unit not found

    return unitsData[currentUnitIndex - 1].id;
};

/**
 * Get unit ID from lesson ID
 */
const getUnitIdFromLessonId = (lessonId: string): string | null => {
    for (const unit of unitsData) {
        if (unit.lessons.some(lesson => lesson.id === lessonId)) {
            return unit.id;
        }
    }
    return null;
};

/**
 * Check if a lesson should be accessible based on unit progression
 */
const isLessonAccessible = (lessonId: string, unitLessons: string[]): boolean => {
    const currentLessonIndex = unitLessons.indexOf(lessonId);
    const currentUnitId = getUnitIdFromLessonId(lessonId);

    if (!currentUnitId) return false;

    // If it's the first lesson of the unit, check if previous unit is completed
    if (currentLessonIndex === 0) {
        const previousUnitId = getPreviousUnitId(currentUnitId);

        // First unit (unit-1) is always accessible
        if (!previousUnitId) return true;

        // For other units, check if previous unit is completed
        return isUnitCompleted(previousUnitId);
    }

    // For non-first lessons, check if previous lessons in the same unit are completed
    return unitLessons
        .slice(0, currentLessonIndex)
        .every(id => isLessonFullyCompleted(id));
};

/**
 * Get lesson progress state based on completion status and unit progression
 * Returns 'finish', 'on-progress', or 'not-started'
 */
export const getLessonState = (lessonId: string, unitLessons: string[]): 'finish' | 'on-progress' | 'not-started' => {
    const userData = getUserData();
    if (!userData) return 'not-started';

    // Check if current lesson is fully completed (5/5)
    const isCurrentLessonFullyCompleted = isLessonFullyCompleted(lessonId);

    if (isCurrentLessonFullyCompleted) {
        return 'finish';
    }

    // Check if the lesson is accessible based on unit progression
    if (!isLessonAccessible(lessonId, unitLessons)) {
        return 'not-started';
    }

    // Find the current lesson index
    const currentLessonIndex = unitLessons.indexOf(lessonId);

    // For lessons that are accessible, determine if they should be 'on-progress'
    // Find the first available lesson that's not fully completed within the accessible lessons
    const firstAvailableIndex = unitLessons.findIndex((id, index) => {
        // Check if this lesson is accessible and not completed
        return isLessonAccessible(id, unitLessons) && !isLessonFullyCompleted(id);
    });

    if (currentLessonIndex === firstAvailableIndex) {
        return 'on-progress';
    }

    return 'not-started';
};

/**
 * Get lesson progress for on-progress lessons
 * Returns current completion count and total required (5)
 */
export const getLessonProgress = (lessonId: string, unitLessons: string[]): { currentLesson: number; totalLessons: number } => {
    const userData = getUserData();
    if (!userData) return { currentLesson: 0, totalLessons: 5 };

    // Get the current completion count for this specific lesson
    const completionCount = getLessonCompletionCount(lessonId);

    // Return progress as "X of 5" where X is the current completion count
    return {
        currentLesson: completionCount, // Current completions (0-5)
        totalLessons: 5 // Always 5 completions required
    };
};/**
 * Get unit completion percentage based on fully completed lessons
 */
export const getUnitProgress = (unitLessons: string[]): number => {
    const userData = getUserData();
    if (!userData) return 0;

    // Count lessons that are fully completed (5/5)
    const fullyCompletedCount = unitLessons.filter(id => isLessonFullyCompleted(id)).length;

    return Math.round((fullyCompletedCount / unitLessons.length) * 100);
};

/**
 * Complete a lesson from UI with rewards and validation
 * This is the main function to call when a user completes a lesson
 * Now uses the new incremental completion system (requires 5 completions)
 */
export const completeLessonFromUI = (lessonId: string, earnedMoney: number = 10, earnedXp: number = 15): boolean => {
    console.log('🎯 completeLessonFromUI called:', { lessonId, earnedMoney, earnedXp });
    console.log('📍 Call stack:', new Error().stack);

    const currentCount = getLessonCompletionCount(lessonId);
    console.log(`📊 Current completion count for ${lessonId}: ${currentCount}/5`);

    // If already at 5 completions, don't increment further
    if (currentCount >= 5) {
        console.warn('⚠️ Lesson already fully completed (5/5):', lessonId);
        // Still award money and reduced XP for practice
        addMoney(earnedMoney);
        addXp(Math.floor(earnedXp * 0.3)); // 30% XP for practice
        console.log(`💰 Earned ${earnedMoney} money and ${Math.floor(earnedXp * 0.3)} XP for practice on completed lesson:`, lessonId);
        return true;
    }

    // Increment the completion count
    console.log('🔄 About to increment lesson completion...');
    const newCount = incrementLessonCompletion(lessonId);
    console.log('🔄 Increment completed, new count:', newCount);

    if (newCount > currentCount) {
        // Award money and XP for completing the lesson
        addMoney(earnedMoney);

        // Award more XP for first completion of each step
        const xpMultiplier = newCount === 5 ? 2 : 1; // Double XP for fully completing lesson
        const totalXp = earnedXp * xpMultiplier;
        addXp(totalXp);

        console.log(`💰 Earned ${earnedMoney} money and ${totalXp} XP. Lesson progress: ${newCount}/5 for ${lessonId}`);

        if (newCount === 5) {
            console.log(`🎉 Lesson fully completed! ${lessonId} is now finished.`);
        }

        return true;
    }

    console.error('❌ Failed to increment lesson completion:', lessonId);
    return false;
};

/**
 * Aksara Progress Management
 * Each aksara character requires 3 lessons to be mastered
 */

/**
 * Get aksara character completion count (0-3)
 */
export const getAksaraCompletionCount = (aksaraChar: string): number => {
    const userData = getUserData();
    if (!userData) return 0;

    // Use the aksaraCompleted array differently - store completion counts
    const aksaraProgress = userData.progress.lessonProgress[`aksara_${aksaraChar}`] || 0;
    return Math.min(3, aksaraProgress); // Cap at 3 lessons
};

/**
 * Increment aksara character completion count
 * Returns the new completion count
 */
export const incrementAksaraCompletion = (aksaraChar: string): number => {
    console.log('📚 incrementAksaraCompletion called for:', aksaraChar);

    const userData = getUserData();
    if (!userData) {
        console.error('❌ No user data found');
        return 0;
    }

    const aksaraKey = `aksara_${aksaraChar}`;
    const currentCount = userData.progress.lessonProgress[aksaraKey] || 0;
    const newCount = Math.min(3, currentCount + 1); // Cap at 3

    console.log(`📊 Incrementing ${aksaraChar}: ${currentCount} → ${newCount}`);

    userData.progress.lessonProgress[aksaraKey] = newCount;

    // If aksara reaches 3 completions, add it to aksaraCompleted for backward compatibility
    if (newCount === 3 && !userData.progress.aksaraCompleted.includes(aksaraChar)) {
        userData.progress.aksaraCompleted.push(aksaraChar);
        console.log(`🎉 Aksara ${aksaraChar} fully mastered and added to aksaraCompleted`);
    }

    const success = saveUserData(userData);

    if (success) {
        console.log(`✅ Aksara ${aksaraChar} completion saved: ${newCount}/3`);
    } else {
        console.error('❌ Failed to save aksara progress');
    }

    return newCount;
};

/**
 * Get aksara character status based on completion count
 */
export const getAksaraStatus = (aksaraChar: string): 'not-started' | 'active' | 'mastered' => {
    const completionCount = getAksaraCompletionCount(aksaraChar);

    if (completionCount === 0) return 'not-started';
    if (completionCount >= 3) return 'mastered';
    return 'active';
};

/**
 * Complete an aksara character lesson from UI
 */
export const completeAksaraFromUI = (aksaraChar: string, earnedMoney: number = 5, earnedXp: number = 10): boolean => {
    console.log('🎯 completeAksaraFromUI called:', { aksaraChar, earnedMoney, earnedXp });

    const currentCount = getAksaraCompletionCount(aksaraChar);
    console.log(`📊 Current completion count for ${aksaraChar}: ${currentCount}/3`);

    // If already at 3 completions, don't increment further
    if (currentCount >= 3) {
        console.warn('⚠️ Aksara already fully mastered (3/3):', aksaraChar);
        // Still award money and reduced XP for practice
        addMoney(earnedMoney);
        addXp(Math.floor(earnedXp * 0.3)); // 30% XP for practice
        console.log(`💰 Earned ${earnedMoney} money and ${Math.floor(earnedXp * 0.3)} XP for practice on mastered aksara:`, aksaraChar);
        return true;
    }

    // Increment the completion count
    const newCount = incrementAksaraCompletion(aksaraChar);

    if (newCount > currentCount) {
        // Award money and XP for completing the aksara lesson
        addMoney(earnedMoney);

        // Award more XP for mastering the aksara
        const xpMultiplier = newCount === 3 ? 2 : 1; // Double XP for mastering
        const totalXp = earnedXp * xpMultiplier;
        addXp(totalXp);

        console.log(`💰 Earned ${earnedMoney} money and ${totalXp} XP. Aksara progress: ${newCount}/3 for ${aksaraChar}`);

        if (newCount === 3) {
            console.log(`🎉 Aksara fully mastered! ${aksaraChar} is now completed.`);
        }

        return true;
    }

    console.error('❌ Failed to increment aksara completion:', aksaraChar);
    return false;
};
